import React, { FC, memo, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import Typography from '@mui/material/Typography';
import { useStyles } from './MyPeopleEvalReportTable.styles';
import { paths } from '@shared/enums/paths';
import { Table } from '@shared/components/Table';
import { MyTeamEvalData } from '@modules/EvaluationModule/interfaces/MyTeamEvalData';
import { ActionButtons } from '../../shared/ActionButtons';
import { useSelectEvals } from '../../hooks/useSelectEvals';
import { EvalStatus } from '../../shared/EvalStatus/EvalStatus';
import classnames from 'classnames';
import { UserReporterBlock } from '@shared/components/UserReporterBlock/UserReporterBlock';
import format from 'date-fns/format';
import { UsersDropdown } from '../MyTeamEvalReportTable/componets/UsersDropdown';
import ReactVirtualizedAutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';
import { VirtualTable } from '@modules/HappinessModule/components/VirtualTable';
import { MY_PEOPLE_EVALS_FETCH_LIMIT } from '@modules/EvaluationModule/pages/MyPeopleEvalPage/constants/pagination';
import { VirtualTableContext } from '@modules/HappinessModule/components/VirtualTable/context/VirtualTableContext';
import { EvaluationStatus } from '@modules/EvaluationModule/enums/EvaluationStatus';
import { EvaluationType } from '@modules/EvaluationModule/enums/EvaluationType';
import { setSelectedEvalsAction } from '@modules/EvaluationModule/pages/MyPeopleEvalPage/redux/actions';
import { SortInfo } from '@shared/interfaces/sortInfo';
import { EvalSortFields } from '@modules/EvaluationModule/pages/MyPeopleEvalPage/enums/EvalSortFields';
import { EoTIndicator } from '../../shared/EoTIndicator/EoTIndicator';

interface Props {
  data: MyTeamEvalData[] | null;
  isDataLoading: boolean;
  loadMoreItems?: (startIndex: number, stopIndex: number) => void | Promise<void>;
  onSort?: (sortInfo: SortInfo) => void;
  sortInfo?: SortInfo;
}

interface RowProps {
  evaluation: MyTeamEvalData;
}

const MyPeopleEvalReportTableComponent: FC<Props> = ({ data, isDataLoading, loadMoreItems, onSort, sortInfo }) => {
  const styles = useStyles();
  const ITEM_HEIGHT = 70;
  const location = useLocation();
  const {
    handleSendTheFormButtonClick,
    handleFailEopButtonClick,
    handlePassEopButtonClick,
    handleClickOnRow,
    startingEvalId,
    failingEopId,
  } = useSelectEvals(setSelectedEvalsAction);

  const tableHeaders = [
    { title: 'Member', fieldName: EvalSortFields.Member },
    { title: 'Month', fieldName: EvalSortFields.Date },
    { title: 'Manager', fieldName: EvalSortFields.Manager },
    { title: '' },
    { title: 'Status', fieldName: EvalSortFields.Status },
    { title: 'Department / Project', fieldName: EvalSortFields.Project },
    { title: 'Location', fieldName: EvalSortFields.Location },
    { title: '' },
  ];

  const tableSkeleton = Array(5)
    .fill(0)
    .map((_, i) => (
      <tr key={i} className={styles.tableDataRow}>
        <td>
          <div className={styles.memberBlock}>
            <div className={styles.personBlock}>
              <Skeleton className={styles.personAvatar} />
              <div className={styles.personInfoBlock}>
                <Skeleton className={styles.personNameSkeleton} />
                <Skeleton className={styles.positionSkeleton} />
              </div>
            </div>
          </div>
        </td>
        <td>
          <Skeleton className={styles.meterSkeleton} />
        </td>
        <td>
          <Skeleton className={styles.meterSkeleton} />
        </td>
        <td>
          <Skeleton className={styles.meterSkeleton} />
        </td>
        <td></td>
        <td>
          <Skeleton className={styles.meterSkeleton} />
        </td>
        <td>
          <Skeleton className={styles.meterSkeleton} />
        </td>
        <td>
          <Skeleton className={styles.meterSkeleton} />
        </td>
      </tr>
    ));

  const isItemLoaded = (index: number) => data !== null && !!data[index];

  const handleSortClick = (sortInfo: SortInfo) => {
    if (onSort) {
      onSort(sortInfo);
    }
  };

  const table = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
    <Table
      isDataLoading={isDataLoading}
      tableHeaders={tableHeaders}
      skeleton={tableSkeleton}
      customStyles={{ tableHeaderRow: styles.tableHeaderRow, tableDataRow: styles.tableDataRow }}
      style={style}
      sortInfo={sortInfo}
      onSortClick={handleSortClick}
    >
      {children}
    </Table>
  );

  const Inner = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(function Inner(
    { children, ...rest },
    ref
  ) {
    const { top } = useContext(VirtualTableContext);
    return (
      <div {...rest} ref={ref}>
        {table({
          children: children,
          style: { top, position: 'absolute', width: '100%' },
        })}
      </div>
    );
  });

  const evalRow = ({ evaluation }: RowProps) => {
    const {
      name,
      imageUrl,
      email,
      position,
      project,
      department,
      userId,
      status,
      evaluationId,
      evaluationDate,
      country,
      city,
      officeCountry,
      peerReviews,
      clientReviews,
      primaryReportsTo,
      typeOfInteraction,
      permissions: { isUpdatable },
      eopResolvedAt,
      eopExtendedTo,
      eopStatus,
      eopNotPassedReason,
      eopExtendedReason,
    } = evaluation;
    const isProcessingEval = startingEvalId !== evaluationId && failingEopId !== evaluationId;
    const isOngoing = status === EvaluationStatus.Ongoing;
    const isEoP = typeOfInteraction === EvaluationType.EndOfProbation;
    const isRowClickable = isOngoing || isEoP;

    return (
      <tr
        key={email}
        onClick={() => isRowClickable && handleClickOnRow(status, userId, evaluationId)}
        className={classnames(styles.tableDataRow, {
          [styles.starting]: startingEvalId === evaluationId,
          [styles.noClickable]: !isRowClickable,
          [styles.canceling]: failingEopId === evaluationId,
        })}
      >
        <td className={styles.cellMember} onClick={(e) => e.stopPropagation()}>
          <div className={styles.memberBlock}>
            <Link
              className={styles.personBlock}
              to={`${paths.users}/${userId}/${paths.evaluations}`}
              state={{ path: location.pathname, name: 'team evaluations' }}
            >
              <UserReporterBlock isDetailInfo user={{ id: userId, email, name, position, imageUrl, country, city }} />
            </Link>
          </div>
        </td>
        <td className={styles.cellMonth}>
          <Typography className={styles.titleText}>{format(new Date(evaluationDate), 'dd MMM yyyy')}</Typography>
        </td>
        <td className={styles.cellManager}>
          {isUpdatable ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <UsersDropdown
                evaluationInfo={evaluation}
                name="Clients"
                linkPath={`${paths.users}/${userId}/${paths.evaluations}/${evaluationId}/${paths.settings}`}
                userReviews={clientReviews}
              />
              <UsersDropdown
                evaluationInfo={evaluation}
                name="Peers"
                linkPath={`${paths.users}/${userId}/${paths.evaluations}/${evaluationId}/${paths.settings}`}
                userReviews={peerReviews}
              />
            </div>
          ) : (
            <div>
              <Typography className={styles.managerName} title={primaryReportsTo.name}>
                {primaryReportsTo.name}
              </Typography>
            </div>
          )}
        </td>
        <td className={styles.cellEopIndicator}>
          <EoTIndicator
            typeOfInteraction={typeOfInteraction}
            eopStatus={eopStatus}
            eopNotPassedReason={eopNotPassedReason}
            eopExtendedReason={eopExtendedReason}
            officeCountry={officeCountry}
          />
        </td>
        <td className={styles.cellStatus}>
          {evaluationDate && status && (
            <EvalStatus
              status={status}
              evaluationDate={evaluationDate}
              eopResolvedAt={eopResolvedAt}
              typeOfInteraction={typeOfInteraction}
              eopExtendedTo={eopExtendedTo}
              eopStatus={eopStatus}
              officeCountry={officeCountry}
            />
          )}
        </td>
        <td className={styles.cellDepartment}>
          {project && <Typography className={styles.projectName}>{project}</Typography>}
          {department && department !== project && (
            <Typography variant="body2" className={styles.departmentName}>
              {department}
            </Typography>
          )}
        </td>
        <td className={styles.cellLocation}>
          <Typography className={styles.titleText}>{officeCountry}</Typography>
        </td>
        <td className={styles.cellActions}>
          {status && (
            <>
              {isProcessingEval ? (
                isUpdatable && (
                  <ActionButtons
                    evaluation={evaluation}
                    handleSendTheFormButtonClick={handleSendTheFormButtonClick}
                    handleFailEopButtonClick={handleFailEopButtonClick}
                    handlePassEopButtonClick={handlePassEopButtonClick}
                    officeCountry={evaluation.officeCountry}
                  />
                )
              ) : (
                <div className={styles.backdrop} onClick={(e) => e.stopPropagation()}></div>
              )}
            </>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.rootVirtualized}>
      {loadMoreItems && (
        <ReactVirtualizedAutoSizer>
          {({ height, width }) => (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={100000}
              loadMoreItems={loadMoreItems}
              minimumBatchSize={MY_PEOPLE_EVALS_FETCH_LIMIT}
              threshold={20}
            >
              {({ onItemsRendered, ref }) => (
                <VirtualTable
                  overscanCount={10}
                  innerElementType={Inner}
                  height={height}
                  width={width}
                  itemCount={data ? data.length : 0}
                  itemSize={ITEM_HEIGHT}
                  children={({ index }) => data && evalRow({ evaluation: data[index] })}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                />
              )}
            </InfiniteLoader>
          )}
        </ReactVirtualizedAutoSizer>
      )}
    </div>
  );
};

export const MyPeopleEvalReportTable = memo(MyPeopleEvalReportTableComponent);
